import React, { useCallback, useEffect, useState } from 'react';

import { FormTemplate, PendingFormItem } from '../interfaces/forms';
import {
  deleteFormTemplate,
  duplicateFormTemplate,
  getFormTemplate,
  getFormTemplates,
  getPendingForms,
  getPesquisaMensagem,
  updateFormTemplate,
  updatePesquisaMensagem,
} from '../../../api/formsApi';
import { mensagemDeErro, notify } from '../../../shared/feedback/notify';
import useIsAdmin from '../../../hooks/useIsAdmin';
import { useIsTi } from '../../../hooks/useUserType';
import FillFormModal from '../modal/FillFormModal';
import FormTemplateModal from '../modal/FormTemplateModal';
import FormResponsesPage from './FormResponsesPage';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';
// Esqueleto de página compartilhado (`recepcao-conte-do`, `recepcao-title`, `recepcao-header-row`).
import '../../recepcao/pages/Recepcao.css';
import '../formularios.css';

type Tab = 'responder' | 'gerenciar';

const FormulariosPage: React.FC = () => {
  const isAdmin = useIsAdmin();
  // TI também gerencia templates de formulário (configuração de sistema — at-25).
  const isTi = useIsTi();
  const canManage = isAdmin || isTi;
  const [activeTab, setActiveTab] = useState<Tab>('responder');
  const [templates, setTemplates] = useState<FormTemplate[]>([]);
  const [manageTemplates, setManageTemplates] = useState<FormTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [fillingTemplate, setFillingTemplate] = useState<FormTemplate | null>(null);
  const [fillingCar, setFillingCar] = useState<PendingFormItem['car'] | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<FormTemplate | 'new' | null>(null);
  const [viewingResponses, setViewingResponses] = useState<{ id: string; title: string } | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [pendingItems, setPendingItems] = useState<PendingFormItem[]>([]);
  const [templateToDelete, setTemplateToDelete] = useState<FormTemplate | null>(null);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [mensagemPesquisa, setMensagemPesquisa] = useState('');
  const [carregandoMensagem, setCarregandoMensagem] = useState(false);
  const [salvandoMensagem, setSalvandoMensagem] = useState(false);

  /** Existe pesquisa do associado ativa? É o que faz sentido ter mensagem para enviar. */
  const temPesquisaPublica = manageTemplates.some(
    (template) => template.respondentePublico && template.isActive,
  );

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await getFormTemplates();
      setTemplates(list);

      if (canManage) {
        const manageList = await getFormTemplates({ manage: true });
        setManageTemplates(manageList);
      }
    } catch (err) {
      // at-64: a faixa existe e tem CSS, mas dizia só "Erro ao carregar formulários" —
      // sem o motivo, quem lê não sabe se é permissão, rede ou dado.
      setError(mensagemDeErro(err, 'Não foi possível carregar os formulários. Recarregue a página; se persistir, acione a TI.'));
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  const loadPendingItems = useCallback(async () => {
    try {
      const items = await getPendingForms();
      setPendingItems(items);
    } catch (err) {
      // Sem o aviso, a lista de pendentes some e parece que não há nada a responder.
      setPendingItems([]);
      notify.errorFrom(
        err,
        'Não foi possível carregar os formulários pendentes. Recarregue a página; se persistir, acione a TI.'
      );
    }
  }, []);

  /**
   * at-67 (BUG-V46): toda ação sobre um template recarrega TAMBÉM as pendências.
   *
   * Ativar, desativar, duplicar, excluir e salvar a edição só chamavam `loadTemplates()`.
   * Como as pendências saem de templates ativos com gatilho, desativar um deles deixava na
   * tela as pendências dele — cards fantasma que, ao serem clicados, abrem um formulário
   * que não deveria mais existir. O inverso também: ativar um formulário com gatilho não
   * fazia as pendências aparecerem até um F5.
   */
  const recarregarTudo = useCallback(() => {
    loadTemplates();
    loadPendingItems();
  }, [loadTemplates, loadPendingItems]);

  useEffect(() => {
    recarregarTudo();
  }, [recarregarTudo]);

  useEffect(() => {
    const handleOutsideClick = (evento: MouseEvent) => {
      // Clique num toast (inclusive no X dele) não é "fora do menu": o `index.js` deixou de parar o
      // `click` dentro do `.Toastify`, porque isso impedia o X de fechar o toast.
      const alvo = evento.target as Element | null;
      if (alvo?.closest?.('.Toastify')) return;
      setOpenMenuId(null);
    };
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  // A mensagem da pesquisa só é buscada quando há pesquisa ativa e a aba está aberta.
  useEffect(() => {
    if (activeTab !== 'gerenciar' || !temPesquisaPublica) return;

    setCarregandoMensagem(true);
    getPesquisaMensagem()
      .then((valor) => setMensagemPesquisa(valor))
      .catch((err) => {
        // Campo vazio pareceria "não há mensagem configurada" — e salvar por cima
        // substituiria o texto em uso.
        notify.errorFrom(
          err,
          'Não foi possível carregar a mensagem enviada ao associado. Recarregue a página; se persistir, acione a TI.'
        );
      })
      .finally(() => setCarregandoMensagem(false));
  }, [activeTab, temPesquisaPublica]);

  const handleSalvarMensagem = async () => {
    setSalvandoMensagem(true);
    try {
      const salva = await updatePesquisaMensagem(mensagemPesquisa);
      setMensagemPesquisa(salva);
      notify.success('Mensagem da pesquisa salva com sucesso.');
    } catch (err) {
      // O motivo do backend importa: mensagem sem {{link}} é recusada com explicação.
      notify.actionError(err, { action: 'update', subject: 'a mensagem da pesquisa' });
    } finally {
      setSalvandoMensagem(false);
    }
  };

  const handleFillTemplate = async (template: FormTemplate) => {
    try {
      const fullTemplate = await getFormTemplate(template.id);
      setFillingCar(null);
      setFillingTemplate(fullTemplate);
    } catch (err) {
      // at-64: o modal simplesmente não abria e a mensagem não dizia por quê.
      notify.errorFrom(err, 'Não foi possível abrir o formulário. Recarregue a página; se persistir, acione a TI.');
    }
  };

  const handleFillPending = async (item: PendingFormItem) => {
    try {
      const fullTemplate = await getFormTemplate(item.templateId);
      setFillingCar(item.car);
      setFillingTemplate(fullTemplate);
    } catch (err) {
      notify.errorFrom(err, 'Não foi possível abrir o formulário pendente. Recarregue a página; se persistir, acione a TI.');
    }
  };

  const handleEditTemplate = async (template: FormTemplate) => {
    try {
      const fullTemplate = await getFormTemplate(template.id);
      setEditingTemplate(fullTemplate);
    } catch (err) {
      notify.errorFrom(err, 'Não foi possível abrir o formulário para edição. Recarregue a página; se persistir, acione a TI.');
    }
  };

  const handleToggleActive = async (template: FormTemplate) => {
    try {
      await updateFormTemplate(template.id, { isActive: !template.isActive });
      notify.success(template.isActive ? 'Formulário desativado.' : 'Formulário ativado.');
      recarregarTudo();
    } catch (err) {
      notify.actionError(err, { action: 'update', subject: 'a situação do formulário' });
    }
  };

  const handleDuplicate = async (template: FormTemplate) => {
    try {
      await duplicateFormTemplate(template.id);
      notify.success('Formulário duplicado com sucesso.');
      recarregarTudo();
    } catch (err) {
      notify.actionError(err, { action: 'create', subject: 'a cópia do formulário' });
    }
  };

  const handleDelete = (template: FormTemplate) => {
    if ((template._count?.responses ?? 0) > 0) {
      notify.warning('Formulários com respostas não podem ser excluídos. Desative-o para tirá-lo de uso.');
      return;
    }
    setTemplateToDelete(template);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;
    try {
      await deleteFormTemplate(templateToDelete.id);
      notify.success('Formulário excluído definitivamente.');
      recarregarTudo();
    } catch (err) {
      // at-67: a extração manual da mensagem do backend virava código repetido em cada
      // tela — é exatamente o que o `notify` faz, e melhor (cobre array, string e fallback).
      notify.actionError(err, { action: 'delete', subject: 'o formulário' });
    } finally {
      setTemplateToDelete(null);
    }
  };

  const isScreenLoading = loading && !templates.length && !manageTemplates.length;

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela mostra',
      conteudo: (
        <p>
          Os formulários que o seu perfil pode responder, em dois grupos: <strong>Gatilhos de Esteiras
          (Pendentes)</strong> — formulários que o sistema abriu automaticamente porque um veículo entrou em uma
          etapa que exige resposta — e <strong>Modelos Disponíveis (Gerais)</strong>, que podem ser respondidos a
          qualquer momento. Cada formulário define quais perfis podem respondê-lo; se algo não aparece para você, é
          porque o seu perfil não está autorizado naquele formulário.
        </p>
      ),
    },
    {
      titulo: 'Pendências de gatilho',
      conteudo: (
        <p>
          Quando um formulário tem gatilho por esteira, todo veículo que entra naquela etapa{' '}
          <strong>sem resposta registrada</strong> vira uma pendência para os perfis autorizados — é a contagem que
          aparece no menu lateral, em <strong>Formulários</strong>. A pendência some quando alguém autorizado
          responde. A pendência também <strong>deixa de existir se o veículo sair da esteira</strong> sem que
          ninguém responda: nesse caso o formulário continua acessível em{' '}
          <strong>Modelos Disponíveis</strong>, marcado como <strong>Também é gatilho de esteira</strong> — é por
          ali que se responde depois.
        </p>
      ),
    },
    {
      titulo: 'Responder',
      conteudo: (
        <p>
          O botão <strong>Responder</strong> abre o formulário; campos marcados com <strong>*</strong> são
          obrigatórios. Formulários vinculados a veículo pedem a escolha da placa (nas pendências de gatilho, o
          veículo já vem definido). <strong>Ver respostas</strong> mostra tudo o que já foi respondido naquele
          formulário, com exportação em CSV.
        </p>
      ),
    },
    ...(canManage
      ? [
          {
            titulo: 'Gerenciar formulários',
            conteudo: (
              <>
                <p>
                  Na aba <strong>Gerenciar</strong>, administradores e TI criam e mantêm os modelos: título, perfis
                  autorizados, vínculo com veículo, gatilho por esteira e os campos (texto, escolha, escala).
                </p>
                <p>
                  Formulário <strong>com respostas não pode ter a estrutura de campos alterada nem ser
                  removido</strong> — as respostas antigas ficariam órfãs. O caminho é <strong>Desativar</strong>{' '}
                  (tira de uso, preservando o histórico) ou <strong>Duplicar</strong> para criar uma nova versão e
                  evoluir os campos nela.
                </p>
                <p>
                  <strong>Cada esteira aceita um formulário automático por vez.</strong> A cópia feita por{' '}
                  <strong>Duplicar</strong> nasce <strong>inativa</strong> e mantém o gatilho do original: ao
                  ativá-la, o sistema pede que o outro seja desativado antes — dois formulários ativos na mesma
                  esteira gerariam duas pendências para o mesmo veículo.
                </p>
                <p>
                  Só aparecem como <strong>perfis autorizados</strong> os que têm acesso à tela de Formulários.
                  Autorizar quem não alcança a tela não faz o formulário chegar a essa pessoa.
                </p>
              </>
            ),
          },
          {
            titulo: 'Pesquisa respondida pelo associado',
            conteudo: (
              <>
                <p>
                  Um formulário pode ser marcado como <strong>pesquisa respondida pelo associado</strong>. Ele não
                  depende de perfis autorizados: quem responde não tem login. A Recepção e o Chefe de Oficina
                  enviam o link pela ação <strong>Enviar Pesquisa de Satisfação</strong>, na aba{' '}
                  <strong>Entregues</strong>, e a mensagem sai pelo WhatsApp do próprio atendente.
                </p>
                <p>
                  O associado também pode responder no <strong>totem da oficina</strong> (endereço{' '}
                  <strong>/pesquisa-totem</strong>, deixado aberto no dispositivo): ele digita a placa e o
                  celular cadastrado, e responde na hora. Vale para veículos entregues nos últimos 30 dias, e a
                  resposta é a mesma — quem respondeu por um canal não responde pelo outro. Se os dados não
                  conferirem, o totem orienta a procurar a recepção, que pode corrigir o cadastro pela ação{' '}
                  <strong>Editar Carro</strong> da aba Entregues.
                </p>
                <p>
                  Para abrir a pesquisa, o associado confirma o <strong>celular cadastrado no veículo</strong> — é
                  por isso que o envio não acontece quando o atendimento está sem telefone do proprietário. Cada
                  atendimento aceita <strong>uma resposta</strong>: depois de enviada, o link passa a mostrar um
                  agradecimento em vez do formulário.
                </p>
                <p>
                  <strong>Apenas um formulário ativo por vez</strong> pode ser a pesquisa do associado, porque o
                  envio não pergunta qual usar. A mensagem enviada é editável logo acima da lista, e precisa manter
                  o marcador <strong>{'{{link}}'}</strong> — sem ele o associado receberia um texto sem link.
                </p>
                <p>
                  A pesquisa <strong>só pode ser respondida pelo associado, pelo link</strong>. Ela não aparece
                  na aba Responder, não usa gatilho de esteira e não pode ser preenchida por dentro — nem por
                  administradores. É o que garante que nenhuma resposta seja registrada no lugar do cliente.
                </p>
                <p>
                  <strong>Perfis autorizados são opcionais</strong> numa pesquisa do associado — quem responde não
                  tem login. A lista passa a significar quem, além do associado, também responde pela tela{' '}
                  <strong>e vê as respostas recebidas</strong>: deixando em branco, apenas administradores
                  acompanham os resultados.
                </p>
                <p>
                  Nas respostas, quem respondeu pelo link aparece como <strong>Associado</strong>, junto da placa do
                  atendimento.
                </p>
              </>
            ),
          },
        ]
      : []),
    {
      titulo: 'De onde vêm as respostas',
      conteudo: (
        <p>
          Cada resposta registra quem respondeu, quando e — quando o formulário exige — o veículo. As respostas
          não são editáveis depois de enviadas; para corrigir, registre uma nova resposta.
        </p>
      ),
    },
  ];

  return (
    <div
      className="Frame1000004012 formularios-page-shell"
      style={{
        height: isScreenLoading ? '100vh' : 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: isScreenLoading ? 'center' : 'space-between',
        alignItems: isScreenLoading ? 'center' : 'flex-start',
        backgroundColor: isScreenLoading ? '#808080' : 'transparent',
      }}
    >
      {isScreenLoading && <FullScreenLoader />}

      {/*
        Esqueleto COMPARTILHADO de página (13/08/2026 — pedido do Victor: o título ficava
        posicionado diferente das telas do Portal). `recepcao-conte-do` e `recepcao-header-row`
        substituem os paddings inline que replicavam os mesmos valores à mão — e trazem a
        responsividade completa do Recepcao.css (degrau de 1024px e o espaço do menu mobile,
        que a regra própria desta tela não tinha). O import é explícito logo abaixo dos demais,
        preservando a lição do BUG-V74/at-67: nada de depender de CSS carregado por tela alheia.
      */}
      <div className="ConteDo recepcao-conte-do">
        <div className="recepcao-header-row">
          <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Formulários
            <button
              type="button"
              className="help-circle-btn"
              title="Como Funciona"
              onClick={() => setIsHelpOpen(true)}
            >
              ?
            </button>
          </div>
        </div>

        {canManage && (
          <div className="formularios-tabs">
            <button
              className={`formularios-tab ${activeTab === 'responder' ? 'active' : ''}`}
              onClick={() => setActiveTab('responder')}
            >
              Responder
            </button>
            <button
              className={`formularios-tab ${activeTab === 'gerenciar' ? 'active' : ''}`}
              onClick={() => setActiveTab('gerenciar')}
            >
              Gerenciar
            </button>
          </div>
        )}

        {error && <div className="formularios-error">{error}</div>}

        {!loading && activeTab === 'responder' && (
          <div className="formularios-responder-container">
            {/* Section 1: Gatilhos de Esteiras (Pendentes) */}
            <div className="formularios-section">
              <div className="formularios-section-header">
                <span className="section-dot pending"></span>
                <h2 className="formularios-section-title">Gatilhos de Esteiras (Pendentes)</h2>
                <span className="formularios-section-count pending">
                  {pendingItems.length}
                </span>
              </div>

              {pendingItems.length === 0 ? (
                <div className="formularios-empty-section">
                  Nenhum formulário pendente de gatilho no momento.
                </div>
              ) : (
                <div className="formularios-grid">
                  {pendingItems.map((item) => (
                    <div className="formularios-card pending" key={`${item.templateId}-${item.car.id}`}>
                      <div className="formularios-badge-container">
                        <span className="formularios-badge-pending">Gatilho de Esteira</span>
                      </div>
                      <div className="formularios-card-title">{item.templateTitle}</div>
                      <div className="formularios-vehicle-detail">
                        <span className="vehicle-plate">{item.car.plate}</span>
                        <span className="vehicle-model">{item.car.model}</span>
                      </div>
                      <div className="formularios-card-actions">
                        <button
                          className="formularios-btn primary"
                          type="button"
                          onClick={() => handleFillPending(item)}
                        >
                          Responder
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Section 2: Formulários Gerais (Modelos) */}
            <div className="formularios-section" style={{ marginTop: '20px' }}>
              <div className="formularios-section-header">
                <span className="section-dot general"></span>
                <h2 className="formularios-section-title">Modelos Disponíveis (Gerais)</h2>
                <span className="formularios-section-count general">
                  {templates.length}
                </span>
              </div>

              {templates.length === 0 ? (
                <div className="formularios-empty-section">
                  Nenhum formulário geral disponível para o seu perfil.
                </div>
              ) : (
                <div className="formularios-grid">
                  {templates.map((template) => (
                    <div className="formularios-card general" key={template.id}>
                      <div className="formularios-badge-container">
                        {/*
                          at-67 (BUG-V45): formulário com gatilho aparece nesta seção com
                          card IDÊNTICO ao de um modelo geral, o que faz o mesmo formulário
                          parecer dois. Ele continua aqui de propósito — é o único caminho
                          para a **resposta tardia**: veículo que saiu da esteira sem
                          resposta deixa de ser pendência, e sem este card ninguém mais
                          consegue responder. O que faltava era dizer o que ele é.
                        */}
                        {template.triggerEsteiraId ? (
                          <span className="formularios-badge-pending" title="Este formulário também gera pendências automáticas quando um veículo entra na esteira configurada.">
                            Também é gatilho de esteira
                          </span>
                        ) : (
                          <span className="formularios-badge-general">Modelo Geral</span>
                        )}
                      </div>
                      <div className="formularios-card-title">{template.title}</div>
                      {template.description ? (
                        <div className="formularios-card-desc">{template.description}</div>
                      ) : (
                        <div className="formularios-card-desc" style={{ fontStyle: 'italic', color: 'var(--tema-texto-4, #94a3b8)' }}>
                          Sem descrição disponível.
                        </div>
                      )}
                      <div className="formularios-card-actions">
                        <button
                          className="formularios-btn primary"
                          type="button"
                          onClick={() => handleFillTemplate(template)}
                        >
                          Responder
                        </button>
                        <button
                          className="formularios-btn"
                          type="button"
                          onClick={() => setViewingResponses({ id: template.id, title: template.title })}
                        >
                          Ver respostas
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {!loading && activeTab === 'gerenciar' && canManage && (
          <>
            {/*
              Mensagem enviada ao associado junto do link da pesquisa. Fica aqui, e não
              numa tela de configuração à parte, porque é do mesmo assunto dos modelos —
              e só aparece quando existe uma pesquisa pública ativa para enviar.
            */}
            {temPesquisaPublica && (
              <div className="formularios-mensagem-box">
                <label className="formularios-label" htmlFor="mensagem-pesquisa">
                  Mensagem enviada ao associado
                </label>
                <textarea
                  id="mensagem-pesquisa"
                  className="formularios-input formularios-mensagem-textarea"
                  rows={4}
                  value={mensagemPesquisa}
                  onChange={(e) => setMensagemPesquisa(e.target.value)}
                  disabled={carregandoMensagem}
                />
                <div className="formularios-hint">
                  Use <strong>{'{{link}}'}</strong> (obrigatório), <strong>{'{{placa}}'}</strong> e{' '}
                  <strong>{'{{nome}}'}</strong>. O texto abre no WhatsApp do atendente, já preenchido, quando ele
                  usa a ação Enviar Pesquisa de Satisfação na aba Entregues.
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <button
                    type="button"
                    className="formularios-btn primary"
                    onClick={handleSalvarMensagem}
                    disabled={salvandoMensagem || carregandoMensagem}
                  >
                    {salvandoMensagem ? 'Salvando...' : 'Salvar mensagem'}
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
              <div onClick={() => setEditingTemplate('new')} className="CtaSmall MouseHover" style={{ marginRight: 0 }}>
                <div className="ComponentName">+ Novo Formulário</div>
              </div>
            </div>

            {manageTemplates.length === 0 ? (
              <div className="formularios-empty">Nenhum formulário cadastrado.</div>
            ) : (
              <div className="formularios-grid">
                {manageTemplates.map((template) => (
                  <div className="formularios-card" key={template.id}>
                    {/* Top-right menu options */}
                    <div className="formularios-card-header-actions">
                      <button
                        className="formularios-card-menu-btn"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenMenuId(openMenuId === template.id ? null : template.id);
                        }}
                        aria-label="Opções do formulário"
                      >
                        <span></span>
                        <span></span>
                        <span></span>
                      </button>
                      {openMenuId === template.id && (
                        <div className="formularios-card-dropdown" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="formularios-dropdown-item"
                            onClick={() => {
                              setOpenMenuId(null);
                              handleDuplicate(template);
                            }}
                          >
                            Duplicar
                          </button>
                          <button
                            type="button"
                            className="formularios-dropdown-item"
                            onClick={() => {
                              setOpenMenuId(null);
                              handleToggleActive(template);
                            }}
                          >
                            {template.isActive ? 'Desativar' : 'Ativar'}
                          </button>
                          <button
                            type="button"
                            className={`formularios-dropdown-item ${
                              (template._count?.responses ?? 0) > 0 ? 'blocked' : 'danger'
                            }`}
                            title={
                              (template._count?.responses ?? 0) > 0
                                ? `Formulário com ${template._count?.responses} resposta(s) não pode ser excluído. Desative-o para tirá-lo de uso.`
                                : 'Excluir definitivamente este formulário'
                            }
                            onClick={() => {
                              setOpenMenuId(null);
                              handleDelete(template);
                            }}
                          >
                            Remover
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="formularios-card-title">{template.title}</div>
                    {template.description && <div className="formularios-card-desc">{template.description}</div>}
                    <div className="formularios-card-meta">
                      <span
                        className={`formularios-chip ${
                          template.isActive ? 'formularios-chip-active' : 'formularios-chip-inactive'
                        }`}
                      >
                        {template.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                      <span className="formularios-chip">{template._count?.fields ?? 0} campo(s)</span>
                      <span className="formularios-chip">{template._count?.responses ?? 0} resposta(s)</span>
                    </div>
                    <div className="formularios-card-actions">
                      <button className="formularios-btn primary" type="button" onClick={() => handleEditTemplate(template)}>
                        Editar
                      </button>
                      <button
                        className="formularios-btn"
                        type="button"
                        onClick={() => setViewingResponses({ id: template.id, title: template.title })}
                      >
                        Ver respostas
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <FillFormModal
        isOpen={!!fillingTemplate}
        template={fillingTemplate}
        preselectedCar={fillingCar}
        onClose={() => {
          setFillingTemplate(null);
          setFillingCar(null);
        }}
        onSubmitted={() => {
          setFillingTemplate(null);
          setFillingCar(null);
          recarregarTudo();
          loadPendingItems();
        }}
      />

      <FormTemplateModal
        isOpen={!!editingTemplate}
        template={editingTemplate}
        onClose={() => setEditingTemplate(null)}
        onSaved={() => {
          setEditingTemplate(null);
          recarregarTudo();
        }}
      />

      {viewingResponses && (
        <FormResponsesPage
          templateId={viewingResponses.id}
          templateTitle={viewingResponses.title}
          onClose={() => setViewingResponses(null)}
        />
      )}

      <ConfirmModal
        isOpen={!!templateToDelete}
        message={`Excluir definitivamente o formulário "${templateToDelete?.title}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        onConfirm={confirmDelete}
        onCancel={() => setTemplateToDelete(null)}
      />

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona — Formulários"
        secoes={helpSections}
      />
    </div>
  );
};

export default FormulariosPage;
